import {
	type Deck,
	publicArtifactQueryOptions,
	usePublicArtifactRequestAccessMutation,
} from "@/services/artifacts-service.ts";
import { MilkdownViewer } from "@/components/editor/milkdown-viewer/index.tsx";
import { PublicCanvasView } from "@/components/canvas/public-canvas-view.tsx";
import { DeckCanvas } from "@/components/presentation/deck-canvas.tsx";
import { getOneAIUrl } from "@/components/legal-link.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useArtifactNavigation } from "@/hooks/use-artifact-navigation.ts";
import { cn } from "@/utils/cn.ts";
import { m } from "@/paraglide/messages.js";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, FileBox, Lock, LogIn, Mail } from "lucide-react";
import Logo from "@/assets/logo.png";
import { CanvasSignInDialog } from "@/components/canvas/canvas-sign-in-dialog.tsx";
import { useCanvasVisitorSession } from "@/hooks/use-canvas-visitor-session.ts";
import { useCallback, useMemo, useState } from "react";
import { toast } from "@/utils/toast.ts";
import { pageTitle } from "@/utils/page-title.ts";

export const Route = createFileRoute("/shared/$token")({
	head: () => ({
		meta: [
			{ title: pageTitle(m.artifact_public_viewer_title()) },
			{ name: "robots", content: "noindex" },
		],
	}),
	component: SharedArtifactPage,
});

function SharedArtifactPage() {
	const { token } = Route.useParams();
	const { auth } = Route.useRouteContext();
	const visitor = useCanvasVisitorSession(token);

	const { data, isLoading, error } = useQuery({
		...publicArtifactQueryOptions(token, visitor.sessionToken),
		retry: false,
		// The magic link is still being redeemed; asking now would render the
		// login card for a visitor who is one round trip from being signed in.
		enabled: !visitor.isExchanging,
	});

	if (isLoading || visitor.isExchanging) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<FileBox className="h-8 w-8 animate-pulse text-muted-foreground" />
			</div>
		);
	}

	const status = (error as { status?: number } | null)?.status;

	if (error) {
		if (status === 401) {
			return <SignInPrompt token={token} />;
		}
		if (status === 403) {
			return <NoAccessPanel token={token} />;
		}
		return <SharedArtifactError />;
	}

	if (!data) {
		return null;
	}

	return (
		<ArtifactViewer
			data={data}
			token={token}
			isAuthenticated={auth.isAuthenticated}
			sessionToken={visitor.sessionToken}
			exchangeFailed={visitor.exchangeFailed}
		/>
	);
}

function ArtifactViewer({
	data,
	token,
	isAuthenticated,
	sessionToken,
	exchangeFailed,
}: {
	data: {
		artifactId: string;
		spaceId: string;
		/**
		 * Absent from exactly one response: a login-required canvas whose document
		 * the endpoint withheld, where the title would itself be a disclosure. That
		 * shape is handled by `CanvasLoginRequired`, which needs no name.
		 */
		name?: string;
		accessLevel: string;
		kind: "document" | "presentation" | "canvas";
		markdown?: string;
		deck?: Deck;
		images?: Record<string, string>;
		html?: string;
		/** Siblings of a split canvas's entry document; see `PublicCanvasView`. */
		sourceFiles?: { path: string; content: string }[];
		dataApi?: boolean;
		fileApi?: boolean;
		files?: Record<string, string>;
		omittedFiles?: string[];
		fileWrites?: boolean;
		fileBroker?: boolean;
		loginRequired?: boolean;
		signedInAs?: string;
		/** The signed-in oneAI user looking at this link; see `PublicCanvasView`. */
		viewer?: { email: string; isMember: boolean; project?: string };
	};
	/** The visitor's session, when they have signed in to this canvas. */
	sessionToken: string | null;
	/** A magic link was present in the URL but could not be redeemed. */
	exchangeFailed: boolean;
	token: string;
	isAuthenticated: boolean;
}) {
	const [bannerDismissed, setBannerDismissed] = useState(false);
	const imageUrls = useMemo(
		() => new Map(Object.entries(data.images ?? {})),
		[data.images],
	);

	// Canvas gets a chrome-light, full-bleed layout: no top branding bar, no
	// padding around the frame, and only a thin attribution/CTA footer — the
	// sandboxed mini-app uses as much of the viewport as possible.
	if (data.kind === "canvas") {
		// The owner requires a sign-in and this visitor has none, so the endpoint
		// withheld the document itself — there is no `html` to render. The card is
		// the only chrome the platform ever renders unprompted: everywhere else the
		// canvas places its own sign-in button.
		if (data.loginRequired && !data.html) {
			return <CanvasLoginRequired token={token} linkError={exchangeFailed} />;
		}
		return (
			<div className="flex h-svh flex-col bg-background">
				<main className="min-h-0 flex-1">
					<PublicCanvasView
						token={token}
						html={data.html ?? ""}
						sourceFiles={data.sourceFiles}
						viewer={data.viewer ?? null}
						dataApi={data.dataApi ?? false}
						fileApi={data.fileApi ?? false}
						files={data.files}
						omittedFiles={data.omittedFiles}
						fileWrites={data.fileWrites ?? false}
						fileBroker={data.fileBroker ?? false}
						spaceId={data.spaceId}
						artifactId={data.artifactId}
						name={data.name ?? ""}
						signedInAs={data.signedInAs ?? null}
						sessionToken={sessionToken}
					/>
				</main>
				<footer className="flex shrink-0 items-center justify-between gap-3 border-t border-border/50 bg-card px-4 py-1.5">
					<span className="truncate text-xs text-muted-foreground">
						{m.artifact_public_cta_text()}
					</span>
					<PublicViewerCta
						artifactId={data.artifactId}
						isAuthenticated={isAuthenticated}
						compact
					/>
				</footer>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-background flex flex-col">
			<header className="border-b border-border/50 bg-zinc-900">
				<div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
					<a
						href={getOneAIUrl()}
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity shrink-0"
					>
						<img src={Logo} alt="oneAI" className="h-5" />
					</a>
					<span className="text-zinc-600" aria-hidden="true">
						{"|"}
					</span>
					<h1 className="text-sm font-medium text-zinc-100 truncate">
						{data.name}
					</h1>
				</div>
			</header>
			<main className="flex-1 min-h-0">
				{data.kind === "presentation" && data.deck ? (
					// Definite height (not flex-derived) so the deck can measure and
					// scale slides — a flex-derived height resolves to 0 on first paint
					// and the stage renders black.
					<div className="flex h-[calc(100svh-9rem)] min-h-[400px] flex-col p-4">
						<DeckCanvas deck={data.deck} imageUrls={imageUrls} />
					</div>
				) : (
					<MilkdownViewer
						markdown={data.markdown ?? ""}
						spaceId={data.spaceId}
						artifactId={data.artifactId}
						documentTitle={data.name ?? ""}
						// A visitor need not be a member, and the PDF endpoint is
						// member-only; Markdown is rendered in the page and stays.
						pdfExport={false}
					/>
				)}
			</main>
			{!bannerDismissed && (
				<footer className="border-t border-border/50 bg-card">
					<div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
						<p className="text-sm text-muted-foreground">
							{m.artifact_public_cta_text()}
						</p>
						<div className="flex items-center gap-2 shrink-0">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setBannerDismissed(true)}
								className="text-xs text-muted-foreground/60 hover:text-muted-foreground"
							>
								{m.common_dismiss()}
							</Button>
							<PublicViewerCta
								artifactId={data.artifactId}
								isAuthenticated={isAuthenticated}
							/>
						</div>
					</div>
				</footer>
			)}
		</div>
	);
}

/**
 * The shared-viewer call-to-action: authenticated visitors get "open in chat";
 * anonymous visitors get the oneAI link. Shared by the document/presentation
 * footer and the thin canvas footer (`compact` shrinks the anonymous link).
 */
function PublicViewerCta({
	artifactId,
	isAuthenticated,
	compact = false,
}: {
	artifactId: string;
	isAuthenticated: boolean;
	compact?: boolean;
}) {
	const { openArtifactInChat } = useArtifactNavigation();
	if (isAuthenticated) {
		return (
			<Button
				size="sm"
				className="gap-1.5"
				onClick={() => openArtifactInChat(artifactId)}
			>
				{m.artifacts_open_in_chat()}
				<ArrowRight className="h-3.5 w-3.5" />
			</Button>
		);
	}
	return (
		<a
			href={getOneAIUrl()}
			target="_blank"
			rel="noopener noreferrer"
			className={cn(
				"inline-flex shrink-0 items-center gap-1.5 font-medium text-primary transition-colors hover:text-primary/80",
				compact ? "text-xs" : "text-sm",
			)}
		>
			{m.artifact_public_cta_button()}
			<ArrowRight className="h-3.5 w-3.5" />
		</a>
	);
}

function SignInPrompt({ token }: { token: string }) {
	return (
		<div className="min-h-screen bg-background flex items-center justify-center">
			<div className="text-center space-y-4 max-w-sm mx-auto px-4">
				<Lock className="h-12 w-12 text-muted-foreground mx-auto" />
				<h1 className="text-xl font-semibold text-foreground">
					{m.artifact_sign_in_to_view()}
				</h1>
				<p className="text-sm text-muted-foreground">
					{m.artifact_sign_in_description()}
				</p>
				<a
					href={`/auth/login?redirect=/shared/${encodeURIComponent(token)}`}
					className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
				>
					<LogIn className="h-4 w-4" />
					{m.artifact_sign_in_to_view()}
				</a>
			</div>
		</div>
	);
}

function NoAccessPanel({ token }: { token: string }) {
	const navigate = useNavigate();
	const { mutate: requestAccess, isPending } =
		usePublicArtifactRequestAccessMutation();
	const [requested, setRequested] = useState(false);

	const handleRequest = useCallback(() => {
		requestAccess(
			{ params: { path: { token } }, body: {} },
			{
				onSuccess: () => {
					toast.success(m.artifact_request_access_success());
					setRequested(true);
				},
				onError: () => {
					toast.error(m.artifact_request_access_error());
				},
			},
		);
	}, [requestAccess, token]);

	return (
		<div className="min-h-screen bg-background flex items-center justify-center">
			<div className="flex flex-col items-center justify-center gap-4 text-center max-w-md mx-auto px-4">
				<Lock className="h-12 w-12 text-muted-foreground" />
				<h1 className="text-xl font-semibold">
					{m.artifact_landing_no_access_title()}
				</h1>
				<p className="text-sm text-muted-foreground">
					{m.artifact_landing_no_access_description()}
				</p>
				<div className="flex items-center gap-2">
					<Button
						className="gap-2"
						onClick={handleRequest}
						disabled={isPending || requested}
					>
						<Mail className="h-4 w-4" />
						{requested
							? m.artifact_access_requested()
							: m.artifact_request_access()}
					</Button>
					<Button
						variant="ghost"
						onClick={() => navigate({ to: "/dashboard/artifacts" })}
					>
						{m.artifact_open_go_back()}
					</Button>
				</div>
			</div>
		</div>
	);
}

function SharedArtifactError() {
	return (
		<div className="min-h-screen bg-background flex items-center justify-center">
			<div className="text-center space-y-4">
				<FileBox className="h-12 w-12 text-muted-foreground mx-auto" />
				<h1 className="text-xl font-semibold text-foreground">
					{m.artifact_public_not_found()}
				</h1>
				<p className="text-muted-foreground">
					{m.artifact_public_not_found_description()}
				</p>
			</div>
		</div>
	);
}

/**
 * The standalone sign-in gate for a canvas whose owner requires it.
 *
 * Rendered instead of the frame, because the endpoint withholds the document
 * itself in that state rather than merely refusing its data — an owner who ticks
 * "require login" expects strangers not to read the page.
 *
 * Deliberately says nothing about the canvas beyond its existence: no name, no
 * description, no owner. A visitor who is not signed in has been told only that
 * something is here and that signing in is the way to see it.
 */
function CanvasLoginRequired({
	token,
	linkError,
}: {
	token: string;
	linkError: boolean;
}) {
	const [open, setOpen] = useState(false);
	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-6">
			<div className="w-full max-w-sm text-center">
				<img src={Logo} alt="oneAI" className="mx-auto mb-6 h-8 w-auto" />
				<Lock className="mx-auto mb-4 h-8 w-8 text-muted-foreground" />
				<h1 className="mb-2 font-semibold text-foreground text-lg">
					{m.canvas_sign_in_required_title()}
				</h1>
				<p className="mb-6 text-muted-foreground text-sm">
					{m.canvas_sign_in_required_description()}
				</p>
				{linkError && (
					<p className="mb-4 text-destructive text-sm">
						{m.canvas_sign_in_link_invalid()}
					</p>
				)}
				<Button onClick={() => setOpen(true)} className="w-full">
					<LogIn className="mr-2 h-4 w-4" />
					{m.canvas_sign_in_title()}
				</Button>
				<CanvasSignInDialog token={token} open={open} onOpenChange={setOpen} />
			</div>
		</div>
	);
}
